import React, { useEffect, useState } from "react";
import { Container, Card, Badge, Spinner } from "react-bootstrap";

const API_BASE = "https://autonotify-api.mobilibrasil.com";
const TOKEN = "Bearer AC48311f6664bbc01c45e34a5e33abff8e";

const RealTimeMonitor = () => {
  const [latestAlert, setLatestAlert] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  const formatDate = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    return date.toLocaleString("pt-BR", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit", second: "2-digit"
    });
  };

// ───────────────────────────────────────────────────────────
// Função: gera uma linha do tempo rica e ordenada cronologicamente
// ───────────────────────────────────────────────────────────
const getVisualTimeline = (alert, logs) => {
  const steps = [];

  const hasLog = (content) =>
    logs.some((log) => log.message.toLowerCase().includes(content.toLowerCase()));

  // Só adiciona "resumo" se não há no banco de logs

  // 4️⃣ Logs reais já salvos no banco (alert_log)
  logs.forEach((log) =>
    steps.push({
      message: `${log.message}`,
      timestamp: log.timestamp,
      status: "real",
    })
  );

 

  // 5️⃣ Escalonamento (dados da API /escalations)
  if (alert.escalations && alert.escalations.length > 0) {
    alert.escalations.forEach((esc, idx) => {
      const tent = esc.attempts || 0;
      steps.push({
        message: `Escalonamento para ${esc.contact_name} (${esc.phone_number}) — ${tent}º tentativa`,
        timestamp: new Date().toISOString(),
        status: "real",
      });
    });
  }

  // 6️⃣ Se ainda não confirmado, mostre etapas em andamento
  if (!alert.confirmed_by) {
    steps.push(
      {
        message: `Tentando contato com ${alert.escalations?.[0]?.contact_name || "contato designado"}...`,
        timestamp: new Date().toISOString(),
        status: "waiting",
      },
      {
        message: "Aguardando confirmação...",
        timestamp: new Date().toISOString(),
        status: "waiting",
      }
    );
  } else {
    // Confirmado: passo final
    steps.push({
      message: `Alerta confirmado por ${alert.confirmed_by}`,
      timestamp: alert.confirmed_at || new Date().toISOString(),
      status: "done",
    });

    if (alert.response_time) {
      steps.push({
        message: `Tempo de resposta: ${alert.response_time} min`,
        timestamp: alert.confirmed_at || new Date().toISOString(),
        status: "done",
      });
    }
  }

  return steps;
};


  const fetchLatestAlert = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/alerts/latest_id`, {
        headers: { Authorization: TOKEN }
      });
      const { id: latestId } = await res.json();
      if (!latestId || latestId === latestAlert?.id) return;

      const alertRes = await fetch(`${API_BASE}/api/alerts`, {
        headers: { Authorization: TOKEN }
      });
      const alertData = await alertRes.json();
      const latest = alertData.find((a) => a.id === latestId);
      setLatestAlert(latest);

      const logRes = await fetch(`${API_BASE}/api/alerts/${latestId}/logs`, {
        headers: { Authorization: TOKEN }
      });
      const logData = await logRes.json();
      setLogs(logData);
    } catch (err) {
      console.error("Erro ao buscar dados:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLatestAlert();
    const interval = setInterval(fetchLatestAlert, 1000);
    return () => clearInterval(interval);
  }, [latestAlert]);

  const getStatusVariant = (status) => {
    switch (status) {
      case "Confirmado": return "success";
      case "Pendente": return "warning";
      case "Resolvido": return "secondary";
      default: return "dark";
    }
  };

  const timelineSteps = latestAlert ? getVisualTimeline(latestAlert, logs) : [];

  return (
    <Container className="mt-5">
      <style>
        {`
          .timeline-container {
            border-left: 3px solid #ccc;
            padding-left: 15px;
            margin-left: 8px;
          }

          .timeline-step .icon-step {
            font-size: 28px;
            margin-top: 3px;
            color: #999;
            line-height: 1;
          }

          .timeline-step .icon-step.real {
            color: #00C897;
          }

          .timeline-step .icon-step.waiting {
            color: #FF914D;
            animation: pulse 1.5s infinite;
          }

          .timeline-step .icon-step.done {
            color: #198754;
          }

          @keyframes pulse {
            0% { opacity: 1; }
            50% { opacity: 0.3; }
            100% { opacity: 1; }
          }
        `}
      </style>

      <h3 className="text-center text-primary">Monitoramento em Tempo Real</h3>

      {loading ? (
        <div className="text-center mt-5">
          <Spinner animation="border" />
          <p>Carregando alerta...</p>
        </div>
      ) : latestAlert ? (
        <>
          <Card className="p-4 mt-4 shadow">
            <h5><strong>Dispositivo:</strong> {latestAlert.device_name}</h5>
            <p>
              <strong>Alerta:</strong> {latestAlert.trigger_name}<br />
              <strong>Status:</strong>{" "}
              <Badge bg={getStatusVariant(latestAlert.status)}>
                {latestAlert.status}
              </Badge><br />
              <strong>Criticidade:</strong> {latestAlert.severity}<br />
              <strong>Ticket:</strong> {latestAlert.ticket_id || "N/A"}<br />
              <strong>Recebido em:</strong> {formatDate(latestAlert.timestamp)}<br />
              <strong>Atendente:</strong> {latestAlert.confirmed_by || "Não Confirmado"}<br />
              <strong>Telefone:</strong> {latestAlert.confirmed_phone || "N/A"}<br />
              <strong>Tempo de Resposta:</strong>{" "}
              {latestAlert.response_time
                ? `${latestAlert.response_time} min`
                : "Em aberto"}
            </p>
          </Card>

          <Card className="mt-4 p-4">
            <h5 className="mb-4">Timeline</h5>
            <div className="timeline-container">
              {timelineSteps.map((step, index) => (
                <div key={index} className="timeline-step d-flex align-items-start mb-3">
                  <div className={`icon-step me-3 ${step.status}`}>•</div>
                  <div>
                    <div><strong>{step.message}</strong></div>
                    <div className="text-muted" style={{ fontSize: "0.8rem" }}>{formatDate(step.timestamp)}</div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </>
      ) : (
        <p className="text-center mt-4">Nenhum alerta encontrado.</p>
      )}
    </Container>
  );
};

export default RealTimeMonitor;
